import { forwardRef, useLayoutEffect, useRef } from 'react'

import { useGSAP } from '@/composables/useGSAP'

export type QuestionsLikertNumberProps = {
  index: number
  isLeaving?: boolean
  delay?: number
}

const QuestionsLikertNumber = forwardRef<HTMLDivElement, QuestionsLikertNumberProps>(
  ({ index, isLeaving, delay = 0 }, ref) => {
    const { gsap } = useGSAP()

    const path = [
      'M445.073 127.478L457.609 391.685L390.425 394.873L381.918 215.59C376.992 223.39 355.28 228.203 343.579 228.758L327.35 229.528L324.627 172.157L340.857 171.387C365.013 170.241 378.926 160.502 382.04 130.469L445.073 127.478Z',
      'M263.648 218.554C270.199 165.283 304.893 131.104 364.151 128.292C420.389 125.623 461.614 157.335 463.889 205.27C465.895 247.543 447.971 276.387 404.4 298.882L361.189 320.981C351.95 325.581 342.819 332.445 343.159 339.616L468.469 333.67L471.191 391.041L277.565 400.229L275.164 357.596C272.977 319.493 291.207 289.121 327.732 269.608L372.363 245.55C387.12 237.662 397.6 227.329 396.705 208.458C396.042 194.492 382.726 184.91 366.873 185.663C345.737 186.666 331.823 204.349 330.74 229.367L263.648 218.554Z',
      'M361.944 399.251C306.083 401.902 258.944 373.119 254.387 324.914L318.285 308.264C321.839 335.332 339.595 342.812 359.222 341.88C380.736 340.86 393.049 329.305 392.261 312.698C391.491 296.468 378.157 286.508 356.643 287.529L333.619 288.622L331.058 234.648L352.572 233.627C368.802 232.857 378.527 222.56 377.864 208.595C377.201 194.63 365.431 185.731 349.956 186.465C330.329 187.397 316.704 195.231 314.74 225.587L248.025 214.756C252.797 163.839 298.544 131.405 347.233 129.095C398.942 126.641 442.774 157.472 444.672 197.481C445.782 220.882 435.103 242.951 417.074 253.642C442.45 262.274 458.245 284.221 459.535 311.397C461.827 359.709 420.824 396.457 361.944 399.251Z',
      'M369.2 395.88L367.015 349.833L242.083 355.761L239.36 298.39L353.643 131.817L424.224 128.468L431.854 289.256L458.275 288.003L460.997 345.373L434.576 346.627L436.761 392.675L369.2 395.88ZM304.657 295.292L364.67 292.444L361.016 215.447L304.657 295.292Z',
      'M362.636 399.218C309.04 401.761 265.407 367.138 258.477 324.72L322.751 308.052C328.498 333.503 340.665 342.761 359.914 341.848C381.051 340.845 394.731 326.199 393.746 305.44C392.761 284.681 377.737 271.019 356.978 272.004C341.126 272.756 331.923 278.11 324.516 289.432L265.079 288.469L280.13 135.305L435.257 127.944L437.98 185.315L332.674 190.312L327.743 229.888C335.383 223.473 346.455 217.651 369.101 216.577C418.546 214.23 458.565 252.43 460.93 302.252C463.419 354.716 420.007 396.496 362.636 399.218Z',
    ]

    const pathRef = useRef<SVGPathElement>(null)

    useLayoutEffect(() => {
      const ctx = gsap.context(() => {
        if (!pathRef.current) return
        gsap.timeline({ delay }).fromTo(
          pathRef.current,
          {
            scale: 0.01,
            transformOrigin: 'center',
          },
          {
            scale: 1,
            duration: 0.6,
            ease: 'back.out(1.7)',
          },
          0
        )
      })

      return () => ctx.kill()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gsap, pathRef])

    useLayoutEffect(() => {
      if (!isLeaving) return
      const ctx = gsap.context(() => {
        if (!pathRef.current) return
        gsap.timeline().fromTo(
          pathRef.current,
          {
            scale: 1,
            transformOrigin: 'center',
          },
          {
            scale: 0.01,
            duration: 0.5,
            ease: 'back.in(1.7)',
          },
          0
        )
      })

      return () => ctx.kill()
    }, [isLeaving, gsap, pathRef])

    return (
      <div ref={ref} className='absolute bottom-0 right-0 w-[50%] max-h-full'>
        <svg
          className='w-full h-auto text-secondary'
          viewBox='0 0 720 422'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='xMaxYMax meet'
        >
          <path ref={pathRef} className='QuestionsLikert__Background__Number' d={path[index - 1]} fill='currentColor' />

          <path
            className='QuestionsLikert__Background__Part'
            d='M544.647 206.244L532.749 204.499C526.251 203.545 519.64 205.213 514.37 209.135L504.723 216.314C499.456 220.234 495.96 226.089 495.007 232.587L493.263 244.485C492.309 250.983 493.977 257.594 497.898 262.864L505.077 272.511C508.999 277.78 514.852 281.276 521.35 282.229L533.248 283.974C539.746 284.928 546.357 283.26 551.627 279.339L561.274 272.16C566.543 268.238 570.039 262.385 570.993 255.886L572.737 243.989C573.691 237.49 572.023 230.879 568.102 225.609L560.923 215.962C557.001 210.693 551.148 207.198 544.65 206.244H544.647Z'
            fill='currentColor'
          />
          <path
            className='QuestionsLikert__Background__Part'
            d='M758.226 328.622C757.492 318.333 753.295 308.484 746.272 300.784H746.274C740.004 293.504 737.247 290.277 736.101 286.105C735.652 283.164 735.765 279.527 735.939 273.847C735.939 273.84 735.939 273.833 735.939 273.826C735.939 273.773 735.941 273.727 735.943 273.674H735.941C736.108 268.588 735.423 263.543 733.955 258.74C730.534 245.836 721.543 234.496 708.376 228.48C690.902 220.499 671.024 224.163 657.61 236.218C648.857 243.647 639.181 247.22 628.582 246.936C628.582 246.936 628.566 246.936 628.559 246.936C628.508 246.936 628.46 246.934 628.409 246.931C610.321 246.439 592.821 256.487 584.777 273.836C579.969 284.206 579.356 295.426 582.231 305.619C584.052 312.913 587.647 319.73 592.77 325.403H592.768C600.293 334.227 602.728 337.122 603.372 342.892C603.557 345.467 603.474 348.643 603.362 353.048C603.362 353.055 603.362 353.062 603.362 353.069C603.362 353.122 603.36 353.168 603.358 353.221H603.36C603.328 354.404 603.342 355.584 603.4 356.763C603.705 373.883 613.612 390.169 630.298 397.893C647.735 405.964 667.629 402.399 681.103 390.41C689.893 383.024 699.588 379.5 710.187 379.837C710.187 379.837 710.203 379.837 710.21 379.837C710.261 379.837 710.309 379.84 710.36 379.842C728.446 380.424 745.997 370.463 754.126 353.154C757.86 345.207 759.119 336.747 758.224 328.622H758.226Z'
            fill='currentColor'
          />
        </svg>
      </div>
    )
  }
)

export default QuestionsLikertNumber
